import { defineStore } from 'pinia'
import * as VoiceService from '@/admin/services/VoiceService'
import {
  type InboundCallConfigurationDTOV2,
  type OutboundCallConfigurationDTOV2,
  type ProviderGeneralConfigurationDTOV2,
  VoiceConfigurationTabEnum,
  VoiceProvider,
} from '@/admin/types/voice'
import { ref, type Ref } from 'vue'
import { useMenuStore } from '@/admin/stores/menuStore'
import { StateNames } from '@/admin/types/menu'

export const useVoiceStore = defineStore('voice', () => {
  const activeProvider: Ref<VoiceProvider> = ref(VoiceProvider.NONE)
  const defaultProvider: Ref<VoiceProvider> = ref(VoiceProvider.NONE)
  const selectedTabs = ref<{ [key in VoiceProvider]?: VoiceConfigurationTabEnum }>({
    [VoiceProvider.AXIALYS]: VoiceConfigurationTabEnum.GENERAL,
    [VoiceProvider.ODIGO]: VoiceConfigurationTabEnum.GENERAL,
    [VoiceProvider.CTI_INHOUSE]: VoiceConfigurationTabEnum.GENERAL,
    [VoiceProvider.GENESYS]: VoiceConfigurationTabEnum.GENERAL,
    [VoiceProvider.FIVE_9]: VoiceConfigurationTabEnum.GENERAL,
    [VoiceProvider.VOICEBOT]: VoiceConfigurationTabEnum.GENERAL,
  })
  const axialysInboundCallConfigs: Ref<Array<InboundCallConfigurationDTOV2>> = ref([])
  const axialysOutboundCallConfigs: Ref<Array<OutboundCallConfigurationDTOV2>> = ref([])

  const voicebotInboundCallConfigs: Ref<Array<InboundCallConfigurationDTOV2>> = ref([])

  const odigoInboundCallConfigs: Ref<Array<InboundCallConfigurationDTOV2>> = ref([])
  const odigoOutboundCallConfigs: Ref<Array<OutboundCallConfigurationDTOV2>> = ref([])

  const genesysInboundCallConfigs: Ref<Array<InboundCallConfigurationDTOV2>> = ref([])
  const genesysOutboundCallConfigs: Ref<Array<OutboundCallConfigurationDTOV2>> = ref([])

  const inhouseInboundCallConfigs: Ref<Array<InboundCallConfigurationDTOV2>> = ref([])
  const inhouseOutboundCallConfigs: Ref<Array<OutboundCallConfigurationDTOV2>> = ref([])

  const five9InboundCallConfigs: Ref<Array<InboundCallConfigurationDTOV2>> = ref([])
  const five9OutboundCallConfigs: Ref<Array<OutboundCallConfigurationDTOV2>> = ref([])

  async function initVoice() {
    defaultProvider.value = await VoiceService.getDefaultVoiceProvider()
    const menuStore = useMenuStore()
    menuStore.entryStates.set(StateNames.CTI_PROVIDER, defaultProvider.value.toString())
  }

  async function updateDefaultProvider(voiceProvider: VoiceProvider) {
    try {
      const result = await VoiceService.putDefaultVoiceConfiguration(voiceProvider)
      defaultProvider.value = <VoiceProvider>result?.defaultProvider ?? VoiceProvider.NONE
      const menuStore = useMenuStore()
      if (result?.defaultProvider) {
        menuStore.entryStates.set(StateNames.CTI_PROVIDER, result.defaultProvider)
      } else {
        menuStore.entryStates.delete(StateNames.CTI_PROVIDER)
      }
    } catch (message) {
      console.error(message)
    }
  }

  async function getSpecificProviderConfiguration(provider: VoiceProvider): Promise<ProviderGeneralConfigurationDTOV2 | undefined> {
    try {
      const configurations = (await VoiceService.getAllProvidersConfigurations()) ?? []
      return configurations.find((item) => item.provider === provider)
    } catch (message) {
      console.error(message)
    }
  }

  async function getGenesysInboundCallConfig() {
    try {
      const configuration = await getSpecificProviderConfiguration(VoiceProvider.GENESYS)
      if (!configuration) throw new Error('General configuration for Genesys not found')
      genesysInboundCallConfigs.value = (await VoiceService.getAllInboundCallConfigurations()).filter(
        (conf) => conf.providerConfiguration === configuration?.id,
      )
    } catch (message) {
      console.error(message)
    }
  }

  async function getInhouseInboundCallConfig() {
    try {
      const configuration = await getSpecificProviderConfiguration(VoiceProvider.CTI_INHOUSE)
      if (!configuration) throw new Error('General configuration for Unify CTI not found')
      inhouseInboundCallConfigs.value = (await VoiceService.getAllInboundCallConfigurations()).filter(
        (conf) => conf.providerConfiguration === configuration?.id,
      )
    } catch (message) {
      console.error(message)
    }
  }

  async function deleteInboundCallConfig(callConfigId: UUID) {
    try {
      await VoiceService.deleteInboundCallConfiguration(callConfigId)
    } catch (message) {
      console.error(message)
    }
  }

  async function getGenesysOutboundCallConfig() {
    try {
      const configuration = await getSpecificProviderConfiguration(VoiceProvider.GENESYS)
      if (!configuration) throw new Error('General configuration for Genesys not found')
      genesysOutboundCallConfigs.value = (await VoiceService.getAllOutboundCallConfigurations()).filter(
        (conf) => conf.providerConfiguration === configuration?.id,
      )
    } catch (message) {
      console.error(message)
    }
  }

  async function getInhouseOutboundCallConfig() {
    try {
      const configuration = await getSpecificProviderConfiguration(VoiceProvider.CTI_INHOUSE)
      if (!configuration) throw new Error('General configuration for Unify CTI not found')
      inhouseOutboundCallConfigs.value = (await VoiceService.getAllOutboundCallConfigurations()).filter(
        (conf) => conf.providerConfiguration === configuration?.id,
      )
    } catch (message) {
      console.error(message)
    }
  }

  async function deleteOutboundCallConfig(callConfigId: UUID) {
    try {
      await VoiceService.deleteOutboundCallConfiguration(callConfigId)
    } catch (message) {
      console.error(message)
    }
  }

  async function getAxialysInboundCallConfig() {
    try {
      const configuration = await getSpecificProviderConfiguration(VoiceProvider.AXIALYS)
      if (!configuration) throw new Error('General configuration for Axialys not found')
      axialysInboundCallConfigs.value = (await VoiceService.getAllInboundCallConfigurations()).filter(
        (conf) => conf.providerConfiguration === configuration?.id,
      )
    } catch (message) {
      console.error(message)
    }
  }

  async function getAxialysOutboundCallConfig() {
    try {
      const configuration = await getSpecificProviderConfiguration(VoiceProvider.AXIALYS)
      if (!configuration) throw new Error('General configuration for Axialys not found')
      axialysOutboundCallConfigs.value = (await VoiceService.getAllOutboundCallConfigurations()).filter(
        (conf) => conf.providerConfiguration === configuration?.id,
      )
    } catch (message) {
      console.error(message)
    }
  }

  async function getFive9InboundCallConfig() {
    try {
      const configuration = await getSpecificProviderConfiguration(VoiceProvider.FIVE_9)
      if (!configuration) throw new Error('General configuration for Five9 not found')
      five9InboundCallConfigs.value = (await VoiceService.getAllInboundCallConfigurations()).filter(
        (conf) => conf.providerConfiguration === configuration?.id,
      )
    } catch (message) {
      console.error(message)
    }
  }

  async function getFive9OutboundCallConfig() {
    try {
      const configuration = await getSpecificProviderConfiguration(VoiceProvider.FIVE_9)
      if (!configuration) throw new Error('General configuration for Five9 not found')
      five9OutboundCallConfigs.value = (await VoiceService.getAllOutboundCallConfigurations()).filter(
        (conf) => conf.providerConfiguration === configuration?.id,
      )
    } catch (message) {
      console.error(message)
    }
  }

  async function getVoicebotInboundCallConfig() {
    try {
      const configuration = await getSpecificProviderConfiguration(VoiceProvider.VOICEBOT)
      if (!configuration) throw new Error('General configuration for Voicebot not found')
      voicebotInboundCallConfigs.value = (await VoiceService.getAllInboundCallConfigurations()).filter(
        (conf) => conf.providerConfiguration === configuration?.id,
      )
    } catch (message) {
      console.error(message)
    }
  }

  async function getOdigoInboundCallConfig() {
    try {
      odigoInboundCallConfigs.value = await VoiceService.getOdigoInboundCallConfigurations()
    } catch (message) {
      console.error(message)
    }
  }

  async function deleteOdigoInboundCallConfig(callConfigId: UUID) {
    try {
      await VoiceService.deleteOdigoInboundCallConfiguration(callConfigId)
      getOdigoInboundCallConfig()
    } catch (message) {
      console.error(message)
    }
  }

  async function getOdigoOutboundCallConfig() {
    try {
      odigoOutboundCallConfigs.value = await VoiceService.getOdigoOutboundCallConfigurations()
    } catch (message) {
      console.error(message)
    }
  }

  async function deleteOdigoOutboundCallConfig(callConfigId: UUID) {
    try {
      await VoiceService.deleteOdigoOutboundCallConfiguration(callConfigId)
      getOdigoOutboundCallConfig()
    } catch (message) {
      console.error(message)
    }
  }

  return {
    activeProvider,
    defaultProvider,
    selectedTabs,
    axialysInboundCallConfigs,
    axialysOutboundCallConfigs,
    odigoInboundCallConfigs,
    odigoOutboundCallConfigs,
    voicebotInboundCallConfigs,
    genesysInboundCallConfigs,
    genesysOutboundCallConfigs,
    inhouseInboundCallConfigs,
    inhouseOutboundCallConfigs,
    five9InboundCallConfigs,
    five9OutboundCallConfigs,
    initVoice,
    updateDefaultProvider,
    getSpecificProviderConfiguration,
    getAxialysInboundCallConfig,
    getAxialysOutboundCallConfig,
    getVoicebotInboundCallConfig,
    getOdigoInboundCallConfig,
    deleteOdigoInboundCallConfig,
    getOdigoOutboundCallConfig,
    deleteOdigoOutboundCallConfig,
    getGenesysInboundCallConfig,
    deleteInboundCallConfig,
    getGenesysOutboundCallConfig,
    deleteOutboundCallConfig,
    getInhouseOutboundCallConfig,
    getInhouseInboundCallConfig,
    getFive9InboundCallConfig,
    getFive9OutboundCallConfig,
  }
})
